import React from 'react';
import { useQuery } from '@apollo/client';
import { ProxyGraphClients, abgGraphqlClient } from '@atlassian/temp-client-utils';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { Maybe, OrderBillTo, OrderLineItem } from '@css-xp/graphtypes/src/generated-types-new';
import { OrderDetailView } from '@css-xp/base/entitlement-utils';
import { fetchQuotesOrderDetails } from '../queries/fetch-quote-order-details';

export interface OrderSummaryCellProps {
  orderLineItem: Maybe<OrderLineItem>;
  billTo: Maybe<OrderBillTo> | undefined;
  isFlatDiscountSupportEnabled: boolean | undefined;
  quoteId: string | undefined | null;
  quoteLineItemId: string | undefined | null;
  transactionAccountId: string;
  shouldShowLegacyInvoiceId?: boolean;
  isGracePeriodVisibilityEnabled?: boolean;
}

const CACHE_POLICY = 'no-cache';

export const OrderSummaryCell = ({
  orderLineItem,
  billTo,
  isFlatDiscountSupportEnabled,
  quoteId,
  quoteLineItemId,
  transactionAccountId,
  shouldShowLegacyInvoiceId,
  isGracePeriodVisibilityEnabled,
}: OrderSummaryCellProps) => {
  const client = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.SXP), []);
  const { data, loading } = useQuery(fetchQuotesOrderDetails, {
    client,
    variables: {
      id: quoteId,
      txaId: transactionAccountId,
    },
    fetchPolicy: CACHE_POLICY,
    nextFetchPolicy: CACHE_POLICY,
    skip: !quoteId || !shouldShowLegacyInvoiceId,
  });

  if (loading) {
    return (
      <SkeletonLoaderWrapper>
        <SkeletonLoader />
      </SkeletonLoaderWrapper>
    );
  }

  const requiredQuoteLineitem = data?.quote?.lineItems?.find(
    (lineItem: any) => lineItem?.lineItemId === quoteLineItemId
  );

  const quoteOrderLegacyInvoiceId = requiredQuoteLineitem?.metadata?.legacyInvoiceId;
  const directOrderLegacyInvoiceId = orderLineItem?.metadata?.legacyInvoiceId;
  const legacyInvoiceId = quoteId ? quoteOrderLegacyInvoiceId : directOrderLegacyInvoiceId;

  return (
    <td>
      <OrderDetailView
        orderLineItem={orderLineItem}
        billTo={billTo}
        isFlatDiscountSupportEnabled={isFlatDiscountSupportEnabled}
        legacyInvoiceId={shouldShowLegacyInvoiceId ? legacyInvoiceId : null}
        isGracePeriodVisibilityEnabled={isGracePeriodVisibilityEnabled}
      />
    </td>
  );
};
